import { useState } from 'react';
import styled from 'styled-components';
import { Modal } from '@shared/ui/Modal';
import { Button } from '@shared/ui/Button';
import { useListAdvisorSpendQuery, useSetAdvisorLimitMutation } from '@shared/api';
import { apiErrorMessage, fmtUsd, type AdvisorSpend } from '@entities/progress';

/* Администратору: расход руководителей на Эксперта за месяц и личные лимиты.
   Пустое поле — общий лимит (OSNOVA_ADVISOR_MONTHLY_LIMIT_USD). */

const Hint = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 60vh;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Who = styled.div`
  min-width: 0;
  font-size: 14px;

  small {
    display: block;
    color: ${({ theme }) => theme.colors.text.muted};
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Spent = styled.div`
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const LimitForm = styled.form`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};

  input {
    width: 84px;
    padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
    border-radius: ${({ theme }) => theme.radius.sm};
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text.primary};
    font: inherit;
    font-size: 13px;
  }
`;

const Error = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

function LimitRow({ row, onError }: { row: AdvisorSpend; onError: (m: string | null) => void }) {
  const [value, setValue] = useState(row.personal_limit ? String(row.limit_usd) : '');
  const [save, { isLoading }] = useSetAdvisorLimitMutation();
  const dirty = value.trim() !== (row.personal_limit ? String(row.limit_usd) : '');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const raw = value.trim().replace(',', '.');
    const limitUsd = raw === '' ? null : Number(raw);
    if (limitUsd !== null && (!Number.isFinite(limitUsd) || limitUsd < 0)) {
      onError('Лимит — число долларов, не меньше нуля');
      return;
    }
    try {
      await save({ userId: row.user_id, limitUsd }).unwrap();
      onError(null);
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось сохранить лимит');
    }
  }

  return (
    <Row>
      <Who>
        {row.full_name}
        <small>{row.email}</small>
      </Who>
      <Spent>
        {fmtUsd(row.spent_usd)} из {fmtUsd(row.limit_usd)}
        {!row.personal_limit && ' (общий)'}
      </Spent>
      <LimitForm onSubmit={submit}>
        <span>$</span>
        <input
          inputMode="decimal"
          placeholder="общий"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-label={`Лимит в месяц для ${row.full_name}`}
        />
        <Button type="submit" $size="sm" $variant="secondary" disabled={!dirty || isLoading}>
          {isLoading ? '…' : 'Сохранить'}
        </Button>
      </LimitForm>
    </Row>
  );
}

export function LimitsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data, isLoading, isError } = useListAdvisorSpendQuery(undefined, { skip: !open });
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal open={open} title="Лимиты Эксперта" onClose={onClose}>
      <Hint>
        Расход за текущий месяц. Пустое поле — общий лимит для всех руководителей; новый месяц
        начинается с нуля.
      </Hint>
      {isLoading && <Hint>Загрузка…</Hint>}
      {isError && <Error>Не удалось загрузить расход руководителей</Error>}
      {data && data.length === 0 && <Hint>Пока нет руководителей с доступом к Эксперту.</Hint>}
      {data && data.length > 0 && (
        <List>
          {data.map((r) => (
            <LimitRow key={r.user_id} row={r} onError={setError} />
          ))}
        </List>
      )}
      {error && <Error role="alert">{error}</Error>}
    </Modal>
  );
}
